import classNames from "classnames";
import { graphql, useStaticQuery } from "gatsby";
import React, { useCallback, useContext, useMemo, useState } from "react";
import Button from "../atoms/button";
import { Link } from "../atoms/link";
import NotificationEcommerce from "../modules/notification-ecommerce";
import useWhereToBuy from "../../hooks/use-where-to-buy";
import CompareContext from "../context/compare";
import { CDN_BASE_URL } from "../../utility/constants";
import { isOldEnergyLabel } from "../../utility/energy-label-link";
import ReevooBadge from "../atoms/reevoo-badge";
import ProductPrice from "./product-price";
import useStoreLocator from "../../utility/use-store-locator";
import ButtonWTB from "../atoms/button-wtb";
import useReevooTrack from "../../utility/use-reevoo-track";
import RepairabilityIndex from "../atoms/repairability-index";
import { hasEcommerce } from "../../utility/ecommerce";
import CartContext from "../context/cart";
import aa from "search-insights";
import { formatPrice } from "../../utility/prices";
import KlarnaPlacemanet from "../atoms/klarna-placement";
import useEnergyLabelLink from "../../utility/use-energy-label-link";
import Icon from "../atoms/icon";
import { eventInternalLinkClick } from "../../utility/data-layer";
import CircleArrowDown from "../../icons/circle-arrow-down.svg";
import KeyMessages from "../modules/key-messages";
import QuantitySelector from "../atoms/quantity-selector";
import AutocompleteSelect from "../atoms/autocomplete-select";
import { LineModal } from "../modules/assistance-section";
import anchorify from "../../utility/anchorify";
import babelConfig from "../../../babel-config.json";
import ScrollLink from "../../utility/scroll-link";

const KLARNA_CLIENT_ID = process.env.GATSBY_KLARNA_CLIENT_ID;
const KLARNA_ENVIROMENT = process.env.GATSBY_KLARNA_ENVIROMENT;

aa("init", {
  appId: "KAJKCKIXF9",
  apiKey: "a17845db9a2d70a06a8f09e8036e81c7",
});

const ALGOLIA_INDEX_NAME = process.env.GATSBY_ALGOLIA_INDEX_NAME;

const ExtraService = ({
  name,
  descriptionHtml,
  sku,
  special_from_date,
  special_to_date,
  regularPrice,
  specialPrice,
  priceLanguage,
  flagDefault,
  labelManagerLabels,
  position,
  pagePath,
}) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = useCallback(() => setOpen((value) => !value), []);
  const { value, currency } = regularPrice;
  const specialPriceValue = specialPrice?.value !== value ? specialPrice?.value : null;
  const price = specialPriceValue || specialPriceValue === 0 ? specialPriceValue : value;
  const specialPriceNull = !specialPriceValue && !special_from_date && !special_to_date;
  const includedPrice =
    !specialPriceNull &&
    (((value === null || value === 0) && (specialPrice === null || specialPrice === 0)) ||
      (specialPrice === null && value > 0))
      ? 0
      : price;

  const onClickExtraService = useCallback(() => {
    eventInternalLinkClick({
      click_element: "pdp_service",
      position: `pdp_${position}`,
      link_text: sku ? `pdp_${sku}` : null,
      action: !open ? "pdp_open" : "pdp_close",
      slug: pagePath,
    });
  }, [position, open, pagePath, sku]);

  return (
    <div className={`box-accordion${open ? " open" : ""} box-accordion-${sku}`}>
      <button
        className="box-accordion__title"
        onClick={() => {
          toggleOpen();
          onClickExtraService();
        }}
        disabled={!descriptionHtml}
      >
        <span className="box-accordion__title-icon">
          <img
            className="icon-svg"
            src={`${CDN_BASE_URL}/extra-services/${sku}.svg`}
            alt={name || ""}
          />
          {name}
        </span>
        <span className="box-accordion__price">
          {!specialPriceNull &&
            value > 0 &&
            (specialPrice === null || specialPrice === 0 || specialPrice < value) && (
              <span className={`price line-through`}>
                {formatPrice(value, currency, priceLanguage)}
              </span>
            )}
          {includedPrice === 0 && flagDefault
            ? labelManagerLabels.includedService
            : formatPrice(includedPrice, currency, priceLanguage)}
          <div className="box-accordion__button">
            <CircleArrowDown className="circle-arrow-down" />
          </div>
        </span>
      </button>
      {descriptionHtml && (
        <div className="box-accordion__content">
          <div
            className="box-accordion__content-inner"
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        </div>
      )}
    </div>
  );
};

const ProductDetailInfo = ({
  product,
  priceData,
  location,
  extraServices,
  onClickWtb,
  storeLink,
  productTitle,
  productFamilyTitle,
  isTopMobile,
  keyMessagesPdp,
  pagePath,
  compatibleProducts,
  compatibleLinesTitle,
  hasDirectProducts,
}) => {
  const {
    productName,
    energyClass,
    lineId,
    productDescBefore,
    productDescAfter,
    minicard4,
    familiesIds,
    triples,
    shopLink,
    key_prod,
    notificationEcommerce,
    repairabilityIndex,
    assetId,
    aecException,
  } = product;
  const {
    config,
    labelManagerLabels,
    site: { siteMetadata },
  } = useStaticQuery(graphql`
    {
      config: liferayGeneralConfiguration {
        isElMarket
        currency
        isReevooActive
        disableQuantitySelector
      }
      site {
        siteMetadata {
          priceLanguage
        }
      }
      labelManagerLabels: labelManagerLabels {
        plpShowLess
        readLess
        readMore
        plpShowMore
        buyOnHooverShop
        foundRetailers
        buyNow
        showProduct
        addToComparison
        removeFromComparison
        technicalSpecifications
        shippingCosts
        includedService
        addToCart
        additionalServices
        whereToFindSku
        verifyCompitability
        verifyCompitabilityWithLines
        insertSkuProduct
        discoverAllModels
        compatibleAppliance
        incompatibleAppliance
        compatibleModels
        compatibleLines
        noOptionsSelect
        seeAllTechnicalSpec
      }
    }
  `);

  const [lineModalVisible, setLineModalVisible] = useState(false);
  const showLineModal = () => setLineModalVisible(true);
  const closeLineModal = () => setLineModalVisible(false);

  const productFamily = familiesIds && familiesIds.length ? familiesIds[0] : null;

  const productDescription =
    productDescBefore || productDescAfter
      ? [productDescBefore, productDescAfter].filter(Boolean).join("<br/>")
      : minicard4?.length
      ? minicard4.join(", ")
      : "";

  const keySellingPoints = useMemo(() => {
    const familyTriples = productFamily && productFamily.triples;
    if (!triples && !familyTriples) return null;
    const concatTriples = familyTriples ? familyTriples.concat(triples) : triples;
    const triplePoints = concatTriples
      .filter((item) => !!item.keySellingPoint)
      .map((item) => item.keySellingPoint);
    return triplePoints.length && triplePoints;
  }, [productFamily, triples]);

  const [description, setDescription] = useState(false);
  const toggleDescription = useCallback(() => setDescription((value) => !value), []);
  const [descriptionMore, setDescriptionMore] = useState(false);
  const toggleDescriptionMore = useCallback(() => setDescriptionMore((value) => !value), []);

  const { isElMarket } = config;
  const energyClassImg = useMemo(() => {
    return energyClass
      ? `${CDN_BASE_URL}/classe-energetica/${
          isOldEnergyLabel(isElMarket, lineId) ? "el" : "nel"
        }/${encodeURIComponent(energyClass)}.svg`
      : null;
  }, [energyClass, isElMarket, lineId]);

  const LANG = useMemo(
    () => siteMetadata.priceLanguage.split("-")[0].toUpperCase(),
    [siteMetadata.priceLanguage]
  );

  const energyClassUrl = useEnergyLabelLink(product, LANG, config, 0);
  const reevooTrack = useReevooTrack(key_prod);
  const whereToBuyAssetId = useWhereToBuy(assetId);

  const { productIds, toggleProduct } = useContext(CompareContext);
  const onToggleProduct = useCallback(() => toggleProduct(key_prod), [toggleProduct, key_prod]);

  const storeLocatorPage = useStoreLocator();

  const contributoRaee = useMemo(
    () =>
      priceData?.contributoRaee?.[0]?.value
        ? formatPrice(
            JSON.parse(priceData.contributoRaee?.[0].value)?.amount,
            priceData.sellingPrice.currency,
            siteMetadata.priceLanguage
          )
        : null,
    [priceData, siteMetadata.priceLanguage]
  );

  const repairabilityIndexParsed = priceData?.repairabilityIndex?.[0]?.value
    ? JSON.parse(priceData.repairabilityIndex?.[0].value)
    : null;

  const [count, setCount] = useState(1);
  const handleChangeCount = useCallback((event) => {
    setCount(Math.max(Number(event.target.value), 1));
  }, []);

  const toggleCountQuantity = useCallback((operation) => {
    if (operation === "increment") {
      setCount((prev) => prev + 1);
    } else {
      setCount((prev) => prev - 1);
    }
  }, []);

  const { addProduct, adding, loadingCart } = useContext(CartContext);

  const addToCart = useCallback(() => {
    const urlSearchparams = new URLSearchParams(location.search);

    aa("convertedObjectIDsAfterSearch", {
      index: ALGOLIA_INDEX_NAME,
      eventName: "Product added to cart",
      userToken: "user",
      queryID: urlSearchparams.get("queryID"),
      position: urlSearchparams.get("index"),
      objectIDs: [urlSearchparams.get("objectID")],
    });
    addProduct(product, priceData, location, "PDP", lineId === 333 ? count : 1);
  }, [addProduct, product, priceData, location, lineId, count]);

  const optionsProductIds = useMemo(() => {
    return compatibleProducts
      ?.map((item) => [
        { label: item.key_prod, value: item.key_prod },
        {
          label: item?.attributes?.find(
            (attr) => attr.id === babelConfig.headAttributes.productName[item.lineId]
          )?.fieldValue,
          value: item?.attributes?.find(
            (attr) => attr.id === babelConfig.headAttributes.productName[item.lineId]
          )?.fieldValue,
        },
      ])
      .flat();
  }, [compatibleProducts]);

  return (
    <div className={classNames("pdp-main-info__info", isTopMobile && "mobile")}>
      <div className="holder">
        <div className="pdp-main-info__info-block pdp-main-info__product-info">
          <div className="main">
            <h1 className="has-rating name">
              {productFamilyTitle && (
                <span className="pdp-main-info__info-pagename">{productTitle}</span>
              )}
              <span className="productfamily">
                {productFamilyTitle ? productFamilyTitle : productTitle}
              </span>
              <span
                className={classNames(
                  "sku pdp-main-info__sku",
                  !energyClassImg && "pdp-main-info__sku--no-energy-class"
                )}
              >
                {productName}
              </span>
            </h1>
            <div className="pdp-main-info__sku-area">
              {config.isReevooActive?.[0] === "yes" ? (
                <ReevooBadge className="reevoo" productId={key_prod} />
              ) : (
                <div></div>
              )}
              <div className="pdp-main-info__sku-area--energy-rep">
                {energyClassUrl && energyClassImg && (
                  <Link to={energyClassUrl} target="_blank" rel="noreferrer">
                    <div
                      className={classNames(
                        "energy_class",
                        `energy_class--${energyClass.toLowerCase()}`
                      )}
                    >
                      <img src={energyClassImg} alt={energyClass} />
                    </div>
                  </Link>
                )}
                {(!!repairabilityIndex?.[0] ||
                  (hasEcommerce &&
                    priceData?.shopLink &&
                    priceData?.repairabilityIndex?.[0]?.value)) && (
                  <RepairabilityIndex
                    lineId={product.lineId}
                    data={
                      repairabilityIndex[0]?.product?.filter((item) => item.sku === key_prod)[0]
                    }
                    indexValue={
                      repairabilityIndexParsed?.value
                        ? repairabilityIndexParsed.value?.replace(".", ",")
                        : null
                    }
                    link={
                      hasEcommerce && priceData?.pdfRepairabilityLink
                        ? priceData.pdfRepairabilityLink
                        : null
                    }
                  />
                )}
              </div>
            </div>
            {productDescBefore && productDescAfter ? (
              <div
                className={classNames(
                  "pdp-main-info__description--more",
                  descriptionMore ? " open" : ""
                )}
              >
                {productDescBefore}&nbsp;
                <span>{productDescAfter}&nbsp;</span>
                <button className="more product-card__ksp--toggle" onClick={toggleDescriptionMore}>
                  <i>{labelManagerLabels.plpShowMore}</i>
                  <em>{labelManagerLabels.plpShowLess}</em>
                </button>
              </div>
            ) : productDescription ? (
              <h2
                className="description"
                dangerouslySetInnerHTML={{ __html: productDescription }}
              ></h2>
            ) : null}
            {productFamily?.claim && <h3 className="description_2">{productFamily.claim}</h3>}
            {/* description 2 (?) */}

            <div className="pdp-main-info__info-block pdp-main-info__ksp">
              {
                <>
                  {keySellingPoints.length && (
                    <div className="product-card__ksp--before">
                      <ul className="product-card__ksp--list">
                        {keySellingPoints.slice(0, 3).map((kps, i) => (
                          <li key={i}>{kps}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className={classNames("product-card__ksp", description ? " open" : "")}>
                    {keySellingPoints.length > 3 && (
                      <ul className="product-card__ksp--list">
                        {keySellingPoints.slice(3, keySellingPoints.length).map((kps, i) => (
                          <li key={i}>{kps}</li>
                        ))}
                      </ul>
                    )}
                    <div
                      className={classNames(
                        "product-card__ksp-compare",
                        !keySellingPoints.length && "product-card__ksp-compare--onlycompare"
                      )}
                    >
                      {keySellingPoints.length > 3 && (
                        <button
                          className="more product-card__ksp--toggle"
                          onClick={toggleDescription}
                        >
                          <i>{labelManagerLabels.readMore}</i>
                          <em>{labelManagerLabels.readLess}</em>
                        </button>
                      )}
                      {lineId !== 333 && (
                        <label className="product-card__compare">
                          <input
                            className={classNames("product-card__compare-button button", {
                              "product-card__compare-button--active": productIds.includes(key_prod),
                            })}
                            checked={productIds.includes(key_prod)}
                            name={key_prod}
                            onChange={onToggleProduct}
                            type="checkbox"
                          />
                          <span>
                            {productIds.includes(key_prod)
                              ? labelManagerLabels.removeFromComparison
                              : labelManagerLabels.addToComparison}
                          </span>
                        </label>
                      )}
                    </div>
                  </div>
                </>
              }
            </div>
            <div>
              <ScrollLink
                href={"#" + anchorify(labelManagerLabels.technicalSpecifications)}
                className="pdp-main-info__anchorspecs"
              >
                <span>{labelManagerLabels.seeAllTechnicalSpec}</span>
              </ScrollLink>
            </div>
          </div>
        </div>
        {lineId === 333 && (!!hasDirectProducts || (compatibleLinesTitle && !aecException)) ? (
          <div className="pdp-main-info__sku--autocomplete pdp-main-info__info-block">
            {compatibleLinesTitle && (
              <h5 className="compatible-models-finder__autocomplete--verify">
                {labelManagerLabels.compatibleLines} {compatibleLinesTitle}
              </h5>
            )}
            {!!hasDirectProducts && (
              <h5 className="compatible-models-finder__autocomplete--verify">
                {compatibleLinesTitle
                  ? labelManagerLabels.verifyCompitabilityWithLines
                  : labelManagerLabels.verifyCompitability}
              </h5>
            )}
            {!!hasDirectProducts && (
              <AutocompleteSelect
                options={optionsProductIds}
                labelManagerLabels={labelManagerLabels}
                accSku={key_prod}
              />
            )}
            <div className="btns">
              {!!hasDirectProducts && (
                <button className="pdp-main-info__sku--autocomplete-button" onClick={showLineModal}>
                  {labelManagerLabels.whereToFindSku}
                </button>
              )}
              <LineModal open={lineModalVisible} onClose={closeLineModal} />
              {!!hasDirectProducts && compatibleProducts?.length > 0 && (
                <a href={`#${anchorify(labelManagerLabels.compatibleModels)}`}>
                  {labelManagerLabels.discoverAllModels}
                  <span className="icon icon-symbols-chevron-right"></span>
                </a>
              )}
            </div>
          </div>
        ) : lineId === 333 && (!!hasDirectProducts || (compatibleLinesTitle && aecException)) ? (
          <div className="pdp-main-info__sku--autocomplete pdp-main-info__info-block">
            <h5 className="compatible-models-finder__autocomplete--verify">{aecException}</h5>
          </div>
        ) : null}
        <div className="pdp-main-info__actions pdp-main-info__info-block">
          {notificationEcommerce.length > 0 ? (
            <NotificationEcommerce data={notificationEcommerce[0]} />
          ) : null}

          {priceData && (
            <ProductPrice
              data={priceData}
              contributoRaee={contributoRaee}
              origin="pdp"
              part="pdi"
            />
          )}
          <div className="product-card__buttons">
            <div
              className={classNames(
                priceData?.sellingPrice &&
                  !shopLink &&
                  lineId === 333 &&
                  config?.disableQuantitySelector?.[0] !== "yes" &&
                  "product-card__buttons--quantity-selector"
              )}
            >
              {priceData?.sellingPrice &&
                !shopLink &&
                lineId === 333 &&
                config?.disableQuantitySelector?.[0] !== "yes" && (
                  <QuantitySelector
                    count={count}
                    toggleCountQuantity={toggleCountQuantity}
                    handleChangeCount={handleChangeCount}
                  />
                )}
              <span
                className={classNames(
                  "cart_cta_holder cart_cta_holder--has-label",
                  priceData?.sellingPrice &&
                    !shopLink &&
                    lineId === 333 &&
                    config?.disableQuantitySelector?.[0] !== "yes" &&
                    "cart_cta_holder--has-quantity-selector"
                )}
              >
                {priceData?.sellingPrice && !shopLink ? (
                  <button
                    onClick={addToCart}
                    disabled={!!adding || loadingCart}
                    className="button primary red add-to-cart"
                  >
                    {labelManagerLabels.addToCart}
                    <Icon name="system-cart" />
                  </button>
                ) : shopLink ? (
                  <>
                    <Link to={shopLink} className="button primary red add-to-cart" key="shoplink">
                      {labelManagerLabels.buyOnHooverShop}
                      <Icon name="system-cart" />
                    </Link>
                    {/* <em>{labelManagerLabels.shippingCosts}</em> */}
                  </>
                ) : null}
              </span>
            </div>
            {whereToBuyAssetId ? (
              <ButtonWTB
                className={classNames(
                  "cswidget button",
                  priceData?.sellingPrice && (shopLink || !!priceData?.sellingPrice)
                    ? "secondary wtb"
                    : "primary red wtb-card"
                )}
                reevooTrack={onClickWtb || reevooTrack}
                label={labelManagerLabels.foundRetailers || labelManagerLabels.buyNow}
                sku={key_prod}
                assetId={whereToBuyAssetId}
                iconName="system-pin"
                storeLink={storeLink}
              />
            ) : (
              storeLocatorPage && (
                <Button
                  data={{
                    label: labelManagerLabels.foundRetailers || labelManagerLabels.buyNow,
                    link: !!storeLink ? storeLink : storeLocatorPage,
                    target: !!storeLink ? "_blank" : "_self",
                    iconName: "system-location",
                  }}
                  className={classNames(
                    "cswidget button",
                    priceData?.sellingPrice && (shopLink || !!priceData?.sellingPrice)
                      ? "secondary wtb"
                      : "primary red wtb-card wtb-card--sl-page"
                  )}
                  iconName="system-pin"
                ></Button>
              )
            )}
          </div>
          {KLARNA_CLIENT_ID && hasEcommerce && priceData?.sellingPrice?.value ? (
            <KlarnaPlacemanet
              clientId={KLARNA_CLIENT_ID}
              sellingPrice={(priceData.sellingPrice.value * 100).toFixed(0)}
              enviroment={KLARNA_ENVIROMENT}
            />
          ) : null}
        </div>
        {hasEcommerce && priceData && keyMessagesPdp?.length > 2 ? (
          <div className="pdp-main-info__main-benefits pdp-main-info__info-block">
            {keyMessagesPdp?.map((item, i) => (
              <KeyMessages data={item} key={i} position={i + 1} pagePath={pagePath} />
            ))}
          </div>
        ) : null}
        {extraServices?.length && (
          <div className="pdp-main-info__main-benefits pdp-main-info__info-block pdp-main-info__info-block--extra-services">
            <p className="box-accordion__extra-services-title">
              {labelManagerLabels.additionalServices}
            </p>
            {extraServices.map((item, index) => (
              <ExtraService
                {...item}
                key={item.sku}
                priceLanguage={siteMetadata?.priceLanguage}
                flagDefault={item?.default}
                labelManagerLabels={labelManagerLabels}
                position={index + 1}
                pagePath={pagePath}
              />
            ))}
          </div>
        )}
      </div>
      {/* <div className="diagonal_line">
        <div className="diagonal_line__line"></div>
      </div> */}
    </div>
  );
};

export default ProductDetailInfo;
